import { FC, Fragment } from 'react'

import { Box, Table, colors } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { TableItemData } from 'modules/finance/components/FinancialWorkspace/types/table'
import { Amount } from 'modules/project_management/components/Amount'

import { DEFAULT_CURRENCY, dashedRowProps, stickyColumnProps } from '../../../const'
import { Delta } from '../../Delta'
import { LoadingRow } from '../LoadingRow'
import { StickyCell } from '../StickyCell'
import { WEIGHT } from '../const'

interface Props {
  level?: number
  grey?: boolean
  tooltip?: string
  dashed?: boolean
  italic?: boolean
  loading?: boolean
  subLabel?: string
  headerCount: number
  title?: string | null
  data: TableItemData[]
  isUngrouped?: boolean
  isSummarized?: boolean
  comparisonData?: TableItemData[]
  hasSiblingWithChildren?: boolean
  ref?: React.RefObject<HTMLTableRowElement>
  titleWrapper?: React.FC<{ children: React.ReactNode }>
}

export const BasicRow: FC<Props> = ({
  ref,
  data,
  grey,
  title,
  italic,
  dashed,
  tooltip,
  loading,
  subLabel,
  level = 0,
  headerCount,
  isUngrouped,
  titleWrapper,
  isSummarized,
  comparisonData,
  hasSiblingWithChildren,
}) => {
  const { company } = useCurrentSession()
  const { supportedCurrencies, hasComparisonDate } = useFinancialWorkspace()
  const currency = supportedCurrencies.includes(String(company.currency))
    ? company.currency
    : DEFAULT_CURRENCY

  if (loading) {
    return <LoadingRow ref={ref} level={level} />
  }

  const isGrey = !loading && (isSummarized || isUngrouped || grey)
  const isItalic = !loading && (isSummarized || isUngrouped || italic)

  const renderCell = (totalAmountInCents?: string, sticky?: boolean) => (
    <Table.Cell wide sticky={sticky ? stickyColumnProps : undefined}>
      <Box flexDirection="row" justifyContent={sticky ? undefined : 'flexEnd'}>
        <Amount
          size="200"
          italic={isItalic}
          weight={WEIGHT[level]}
          optionalDecimals={false}
          currency={String(currency)}
          color={isGrey ? 'grey800' : 'grey1000'}
          amount={Number(totalAmountInCents) || null}
        />
      </Box>
    </Table.Cell>
  )

  return (
    <Table.Row
      trRef={ref}
      style={{
        height: '56px',
        backgroundColor: isGrey ? colors.grey100 : undefined,
        ...(dashed ? dashedRowProps : {}),
      }}
    >
      {title && (
        <StickyCell
          grey={isGrey}
          level={level}
          title={title}
          dashed={dashed}
          italic={isItalic}
          tooltip={tooltip}
          subLabel={subLabel}
          titleWrapper={titleWrapper}
          hasSiblingWithChildren={hasSiblingWithChildren}
        />
      )}
      {[...Array(headerCount)].map((_, index) => {
        const cell = data[index]
        const compareToData = comparisonData?.[index]
        const sticky = index === 0 && !title
        const withBorder = index != headerCount - 1
        return (
          <Fragment key={index}>
            {hasComparisonDate ? renderCell(compareToData?.totalAmountInCents, sticky) : null}
            {renderCell(cell?.totalAmountInCents, sticky)}
            {hasComparisonDate ? (
              <Table.Cell
                paddingY="s0"
                style={{ borderRight: withBorder ? '1px solid #E2E2E5' : undefined }}
              >
                <Box flexDirection="row" alignItems="center" justifyContent="flexEnd" gap="s8">
                  <Delta
                    noDiffResult="-"
                    secondValue={Number(cell?.totalAmountInCents)}
                    firstValue={Number(compareToData?.totalAmountInCents)}
                  />
                </Box>
              </Table.Cell>
            ) : null}
          </Fragment>
        )
      })}
    </Table.Row>
  )
}
